/**
 * Data Rights Service
 *
 * Service for GDPR/CCPA/FERPA data rights requests:
 * - Export athlete data
 * - Request account anonymization
 * - Cancel anonymization requests
 * - Check request status
 */

import { supabase } from '../../config/supabase';

// ============================================================================
// Types
// ============================================================================

export interface DataRightsStatus {
  pendingAnonymization: {
    request_id: string;
    scheduled_at: string;
    days_remaining: number;
  } | null;
  recentExports: Array<{
    id: string;
    completed_at: string;
    metadata?: {
      export_size?: {
        sessions: number;
        telemetry_records: number;
        gps_points: number;
      };
    };
  }>;
}

export interface ExportResponse {
  success: boolean;
  blob?: Blob;
  filename?: string;
  error?: string;
}

export interface AnonymizeResponse {
  success: boolean;
  request_id?: string;
  scheduled_at?: string;
  error?: string;
}

export interface CancelResponse {
  success: boolean;
  error?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) {
      throw new Error('No authentication token available');
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  } catch (error) {
    console.error('[DataRights] Failed to get auth token:', error);
    throw error;
  }
}

function getApiBaseUrl(): string {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  // Local CRA (:3000) → Vercel API (:3001). Production uses same-origin /api/*.
  return isLocalhost ? 'http://localhost:3001' : '';
}

// ============================================================================
// Data Rights Service Class
// ============================================================================

class DataRightsService {
  /**
   * Get current status of data rights requests
   */
  async getStatus(): Promise<{ success: boolean; data?: DataRightsStatus; error?: string }> {
    try {
      const headers = await getAuthHeaders();
      const apiBase = getApiBaseUrl();
      const url = apiBase ? `${apiBase}/api/data-rights/status` : '/api/data-rights/status';
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        } else {
          // Response is HTML (404 page) or other non-JSON
          throw new Error(
            `API endpoint not found (${response.status}). Please ensure the API is deployed.`
          );
        }
      }

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API returned non-JSON response');
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('[DataRights] Failed to get status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Export personal data as ZIP file (role determined server-side from profiles.user_role).
   */
  async exportData(filters?: {
    sessionIds?: string[];
    dateFrom?: string;
    dateTo?: string;
    includeProfile?: boolean;
    includeSessions?: boolean;
    includeTelemetry?: boolean;
    includeGPS?: boolean;
    includeRecruiting?: boolean;
    includeActivityLogs?: boolean;
  }): Promise<ExportResponse> {
    try {
      const headers = await getAuthHeaders();
      const apiBase = getApiBaseUrl();
      const url = apiBase ? `${apiBase}/api/data-rights/export` : '/api/data-rights/export';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: filters ? JSON.stringify(filters) : undefined,
      });

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        } else {
          throw new Error(
            `API endpoint not found (${response.status}). Please ensure the API is deployed.`
          );
        }
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `glyyd-data-export-${Date.now()}.zip`;

      // Get blob
      const blob = await response.blob();

      // Trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      return { success: true, blob, filename };
    } catch (error) {
      console.error('[DataRights] Failed to export data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Request account anonymization
   * @param password - User's password for verification
   */
  async requestAnonymization(password: string): Promise<AnonymizeResponse> {
    try {
      const headers = await getAuthHeaders();
      const apiBase = getApiBaseUrl();
      const url = apiBase ? `${apiBase}/api/data-rights/anonymize` : '/api/data-rights/anonymize';
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        } else {
          throw new Error(
            `API endpoint not found (${response.status}). Please ensure the API is deployed.`
          );
        }
      }

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API returned non-JSON response');
      }

      const data = await response.json();
      return {
        success: true,
        request_id: data.request_id,
        scheduled_at: data.scheduled_at,
      };
    } catch (error) {
      console.error('[DataRights] Failed to request anonymization:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Cancel pending anonymization request
   * @param request_id - ID of the request to cancel
   */
  async cancelAnonymization(request_id: string): Promise<CancelResponse> {
    try {
      const headers = await getAuthHeaders();
      const apiBase = getApiBaseUrl();
      const url = apiBase ? `${apiBase}/api/data-rights/cancel` : '/api/data-rights/cancel';
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ request_id }),
      });

      if (!response.ok) {
        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}`);
        } else {
          throw new Error(
            `API endpoint not found (${response.status}). Please ensure the API is deployed.`
          );
        }
      }

      return { success: true };
    } catch (error) {
      console.error('[DataRights] Failed to cancel anonymization:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }
}

// ============================================================================
// Export singleton instance
// ============================================================================

export const dataRightsService = new DataRightsService();
